import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { clearCategoryDetails } from "./categorySlice";
import {
  createCategory,
  getAllCategory,
  getCategoryByID,
  updateCategory,
  updateCategoryImage,
} from "./services/category.services";
import { closed } from "../../components/muidrawer/muiDrawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUploadMorden from "../../components/formFields/formImageUploadMorden";
import { slugify } from "../../_helper/helper";

const CreateCategory: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const CategoryDetail = useAppSelector(
    (state) => state.category.CategoryDetail
  );
  const CategoryID = useAppSelector((state) => state.muidrawer.data);

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [CategoryDetail]);

  const loading = useAppSelector((state) => state.category.formloading);

  useEffect(() => {
    return () => {
      dispatch(clearCategoryDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (CategoryID) {
      dispatch(getCategoryByID(CategoryID));
    }
  }, [CategoryID]);

  const handleSubmit = async (values: any) => {
    const insertPayload = {
      Name: values?.Name ? values?.Name : "",
      CategoryURL: values?.CategoryURL ? values?.CategoryURL : "",
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const Image =
      values?.Image &&
      typeof values?.Image !== "string" &&
      Array.isArray(values?.Image) &&
      values?.Image?.length > 0
        ? values.Image[0].getRawFile()
        : "";
    try {
      if (CategoryID) {
        const editPayload = {
          ...insertPayload,
          ID: CategoryID,
        };

        const response = await dispatch(updateCategory(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            values?.Image &&
            typeof values?.Image !== "string" &&
            Array.isArray(values?.Image) &&
            values?.Image?.length > 0
          ) {
            ImageData.append("Image", Image);
            ImageData.append("ID", CategoryID);
            await dispatch(updateCategoryImage(ImageData));
          }
          dispatch(closed());
          dispatch(clearCategoryDetails());
          dispatch(getAllCategory());
        }
      } else {
        const response = await dispatch(createCategory(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            values?.Image &&
            typeof values?.Image !== "string" &&
            Array.isArray(values?.Image) &&
            values?.Image?.length > 0 &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Image", Image);
            ImageData.append("ID", response?.payload?.Data?.ID);
            await dispatch(updateCategoryImage(ImageData));
          }
          dispatch(closed());
          dispatch(clearCategoryDetails());
          dispatch(getAllCategory());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    const slug = slugify(e.value);
    formRenderProps.onChange("CategoryURL", { value: slug });
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        onSubmit={handleSubmit}
        initialValues={CategoryDetail}
        key={formKey}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
              gap={{ rows: 10 }}
              cols={[{ width: "100%" }]}
            >
              <GridLayoutItem
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 20,
                }}
              >
                <Typography.h4 className="m-0">
                  {CategoryID ? "Update Category" : "Add Category"}
                </Typography.h4>
                <Button
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                >
                  <RxCross1 />
                </Button>
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="Name"
                  label="Name"
                  placeholder="Category"
                  onChange={(e) => handleCreateSlug(e, formRenderProps)}
                  component={FormTextField}
                  astrike={true}
                  validator={requiredValidator}
                />
                <Field
                  name="CategoryURL"
                  label="Category URL"
                  placeholder="Category URL"
                  component={FormTextField}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  justifyContent="start"
                  name={`Image`}
                  label="Client Image"
                  type="file"
                  accept="image/*"
                  alternateName="Client Image"
                  component={FormImageUploadMorden}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  id={"IsActive"}
                  name={"IsActive"}
                  label={"Status"}
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status.value,
                      label: status.label,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem
                colSpan={3}
                style={{
                  marginTop: 20,
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={CategoryID ? "Save" : "Add"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || loading}
                  loading={loading}
                />

                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateCategory;
